import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import TopNavbar from '../../../components/TopNavbar';
import SideNavbar from '../../../components/SideNavbar';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useAuth } from '../../../AuthContext';
import TableSkeleton from '../../../components/TableSkeleton';

// redux
import { useAppSelector } from '../../../redux/store';
import getCurrentScreen from '../../../Helper/GetScreen';

interface Lead {
    name: string;
    [key: string]: any;
}

interface User {
    name: string;
    id: string;
    companyId: string;
    isAdmin: boolean;
    [key: string]: any;
}

interface Escalation {
    id: string;
    requestedById: string;
    assigneeId: string;
    createdDate: Timestamp;
    isActive: boolean;
    isDone: boolean;
    description: string;
}

interface EscalationWithNames extends Escalation {
    requestedByName: string;
    assigneeName: string;
}

const EscalationDetailModal = ({
    escalation,
    onClose,
    onMarkAsDone,
    onReopen
}: {
    escalation: EscalationWithNames | null;
    onClose: () => void;
    onMarkAsDone: (id: string) => Promise<void>;
    onReopen: (id: string) => Promise<void>;
}) => {
    if (!escalation) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-montserrat text-gray-900">Escalation Details</h2>
                    <div className="flex items-center gap-4">
                        {escalation.isDone ? (
                            <button
                                onClick={() => onReopen(escalation.id)}
                                className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/refresh.svg`}
                                    alt="Reopen"
                                    className="w-5 h-5"
                                />
                                <span>Reopen</span>
                            </button>
                        ) : (
                            <button
                                onClick={() => onMarkAsDone(escalation.id)}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/check-circle.svg`}
                                    alt="Mark as Done"
                                    className="w-5 h-5"
                                />
                                <span>Mark as Done</span>
                            </button>
                        )}
                        <button onClick={onClose}>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                                alt="Close"
                                className="w-6 h-6"
                            />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <h3 className="font-montserrat text-sm text-gray-500 mb-1">Ticket ID</h3>
                        <p className="font-montserrat text-gray-900">{escalation.id.slice(0, 8)}</p>
                    </div>
                    <div>
                        <h3 className="font-montserrat text-sm text-gray-500 mb-1">Status</h3>
                        <span className={`px-2 py-1 rounded-full ${escalation.isDone ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {escalation.isDone ? 'Done' : 'Pending'}
                        </span>
                    </div>
                    <div>
                        <h3 className="font-montserrat text-sm text-gray-500 mb-1">Student Name</h3>
                        <p className="font-montserrat text-gray-900">{escalation.requestedByName}</p>
                    </div>
                    <div>
                        <h3 className="font-montserrat text-sm text-gray-500 mb-1">Staff Name</h3>
                        <p className="font-montserrat text-gray-900">{escalation.assigneeName}</p>
                    </div>
                    <div>
                        <h3 className="font-montserrat text-sm text-gray-500 mb-1">Created On</h3>
                        <p className="font-montserrat text-gray-900">
                            {escalation.createdDate.toDate().toLocaleDateString()}
                        </p>
                    </div>
                    <div className="col-span-2">
                        <h3 className="font-montserrat text-sm text-gray-500 mb-1">Description</h3>
                        <p className="font-montserrat text-gray-900 whitespace-pre-wrap">{escalation.description}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Escalations = () => {
    
    const { currentUser, isAdmin, isManager, isStaff } = useAuth();
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
    const [loading, setLoading] = useState(true);

    const [escalations, setEscalations] = useState<EscalationWithNames[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    // const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    // const [isManager, setIsManager] = useState<boolean | null>(null);
    const [selectedEscalation, setSelectedEscalation] = useState<EscalationWithNames | null>(null);

    useEffect(() => {
        checkAdminAndFetchEscalations();
    }, [currentUser]);

    const checkAdminAndFetchEscalations = async () => {
        if (!currentUser) return;
        setLoading(true);

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            // setIsAdmin(userData?.isAdmin || false);
            // setIsManager(userData?.isManager || false);

            if (isAdmin || isManager) {
                const sixMonthsAgo = new Date();
                sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

                let escalationsQuery = query(
                    collection(db, `companies/${userData.companyId}/escalations`),
                    where('isActive', '==', true),
                    where('createdDate', '>=', Timestamp.fromDate(sixMonthsAgo))
                );
                
                if (isAdmin && currentScreen === "Admin") {
                    escalationsQuery = query(
                        collection(db, `companies/${userData.companyId}/escalations`),
                        where('isActive', '==', true),
                        where('createdDate', '>=', Timestamp.fromDate(sixMonthsAgo))
                    );
                } else if (isManager && currentScreen === "Manager") {
                    const userQuery = query(
                        collection(db, 'users'),
                        where('companyId', '==', userData.companyId),
                        where('managerId', '==', currentUser?.uid)
                    );

                    const userQuerySnapshot = await getDocs(userQuery);
                    const usersData = userQuerySnapshot.docs.map(doc => doc.id);

                    escalationsQuery = query(
                        collection(db, `companies/${userData.companyId}/escalations`),
                        where('isActive', '==', true),
                        where('createdDate', '>=', Timestamp.fromDate(sixMonthsAgo)),
                        where('assigneeId', 'in', usersData)
                    );
                }

                const escalationsSnapshot = await getDocs(escalationsQuery);
                const escalationsData: EscalationWithNames[] = [];

                for (const escalationDoc of escalationsSnapshot.docs) {
                    const escalationData = escalationDoc.data();

                    // Fetch requestedBy user's name from leads collection
                    const leadDoc = await getDoc(doc(db, `companies/${userData.companyId}/leads/${escalationData.requestedById}`));
                    const leadData = leadDoc.data() as Lead | undefined;

                    // Fetch assignee's name from users collection
                    const assigneeDoc = await getDoc(doc(db, `users/${escalationData.assigneeId}`));
                    const assigneeData = assigneeDoc.data() as User | undefined;

                    escalationsData.push({
                        id: escalationDoc.id,
                        ...escalationData,
                        requestedByName: leadData?.name || 'Unknown',
                        assigneeName: assigneeData?.name || 'Unknown'
                    } as EscalationWithNames);
                }

                setEscalations(escalationsData);
            }
        } catch (error) {
            console.error('Error fetching escalations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (escalationId: string) => {
        if (!currentUser) return;

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            const escalationRef = doc(db, `companies/${userData.companyId}/escalations`, escalationId);
            await updateDoc(escalationRef, {
                isActive: false
            });
            checkAdminAndFetchEscalations(); // Refresh the list
        } catch (error) {
            console.error('Error deleting escalation:', error);
        }
    };

    const handleMarkAsDone = async (escalationId: string) => {
        if (!currentUser) return;

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            const escalationRef = doc(db, `companies/${userData.companyId}/escalations`, escalationId);
            await updateDoc(escalationRef, {
                isDone: true
            });
            setSelectedEscalation(null); // Close the modal
            checkAdminAndFetchEscalations(); // Refresh the list
        } catch (error) {
            console.error('Error marking escalation as done:', error);
        }
    };

    const handleReopen = async (escalationId: string) => {
        if (!currentUser) return;

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            const escalationRef = doc(db, `companies/${userData.companyId}/escalations`, escalationId);
            await updateDoc(escalationRef, {
                isDone: false
            });
            setSelectedEscalation(null); // Close the modal
            checkAdminAndFetchEscalations(); // Refresh the list
        } catch (error) {
            console.error('Error reopening escalation:', error);
        }
    };

    // Filter escalations based on search query (across all fields)
    const filteredEscalations = escalations.filter(escalation => {
        const searchLower = searchQuery.toLowerCase();
        return (
            escalation.requestedByName.toLowerCase().includes(searchLower) ||
            escalation.assigneeName.toLowerCase().includes(searchLower) ||
            escalation.id.toLowerCase().includes(searchLower) ||
            escalation.description.toLowerCase().includes(searchLower) ||
            (escalation.isDone ? 'done' : 'pending').includes(searchLower)
        );
    });

    const tableClass = "px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat";
    const rowClass = "px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat";

    // // If not admin, redirect to home
    // if (isAdmin === false) {
    //     return <Navigate to="/" replace />;
    // }

    return (
        <div className="min-h-screen bg-gray-200">
            {/* <div className="fixed top-0 left-0 w-full z-10">
                <TopNavbar />
            </div> */}

            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md w-full flex flex-col">
                    <div className="flex flex-col">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-6 mb-6">
                                <div
                                    className="flex items-center justify-center w-14 h-14 rounded-full"
                                    style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/solar-flag.svg`}
                                        alt="Escalation Icon"
                                    />
                                </div>
                                <h2 className="font-montserrat text-2xl font-medium-gray-900">Escalations</h2>
                            </div>
                            <p className="text-sm text-gray-500 mb-4">* Showing escalations from the last 6 months only</p>
                        </div>
                        <div className="mb-8">
                            <div className="flex items-center rounded-3xl bg-zinc-100 w-80">
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                    alt="Search Icon"
                                    className="w-5 h-5 ml-2 cursor-pointer"
                                />
                                <input
                                    type="text"
                                    placeholder="Search for escalations..."
                                    className="w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="rounded-xl overflow-hidden bg-white shadow-lg">
                            {loading ? (
                                <TableSkeleton columns={6} rows={5} />
                            ) : (
                            <div className="overflow-x-auto relative">
                                <table className="min-w-full table-auto border-collapse">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-[#02385E] to-[#02385E]/90">
                                            <th className={tableClass}>Student Name</th>
                                            <th className={tableClass}>Created On</th>
                                            <th className={tableClass}>Staff Name</th>
                                            <th className={tableClass}>Ticket ID</th>
                                            <th className={`${tableClass} w-[120px]`}>Status</th>
                                            <th className={tableClass}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredEscalations.map((escalation, index) => (
                                            <tr key={escalation.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                                <td className={rowClass}>{escalation.requestedByName}</td>
                                                <td className={rowClass}>{escalation.createdDate.toDate().toLocaleDateString()}</td>
                                                <td className={rowClass}>{escalation.assigneeName}</td>
                                                <td className={rowClass}>{escalation.id.slice(0, 8)}</td>
                                                <td className={`${rowClass} w-[120px]`}>
                                                    <span className={`inline-block min-w-[80px] px-2 py-1 rounded-full ${escalation.isDone ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                        {escalation.isDone ? 'Done' : 'Pending'}
                                                    </span>
                                                </td>
                                                <td className={rowClass}>
                                                    <div className="flex gap-4">
                                                        <img
                                                            src={`${process.env.PUBLIC_URL}/images/desktop/lucide-view.svg`}
                                                            alt="View Details"
                                                            className="w-5 h-5 cursor-pointer"
                                                            onClick={() => setSelectedEscalation(escalation)}
                                                        />
                                                        {!escalation.isDone ? (
                                                            <img
                                                                src={`${process.env.PUBLIC_URL}/images/desktop/check-circle.svg`}
                                                                alt="Mark as Done"
                                                                className="w-5 h-5 cursor-pointer"
                                                                onClick={() => handleMarkAsDone(escalation.id)}
                                                            />
                                                        ) : (
                                                            <img
                                                                src={`${process.env.PUBLIC_URL}/images/desktop/refresh.svg`}
                                                                alt="Reopen"
                                                                className="w-5 h-5 cursor-pointer"
                                                                onClick={() => handleReopen(escalation.id)}
                                                            />
                                                        )}
                                                        <img
                                                            src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                                            alt="Delete Icon"
                                                            className="w-5 h-5 cursor-pointer"
                                                            onClick={() => handleDelete(escalation.id)}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            )}
                        </div>
                        <hr
                            className="mt-10"
                            style={{
                                border: "none",
                                height: "2px",
                                background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)",
                            }}
                        />
                    </div>
                </div>
            </div>
            {selectedEscalation && (
                <EscalationDetailModal
                    escalation={selectedEscalation}
                    onClose={() => setSelectedEscalation(null)}
                    onMarkAsDone={handleMarkAsDone}
                    onReopen={handleReopen}
                />
            )}
        </div>
    );
};

export default Escalations;
